import {
  Divider,
  IndexPath,
  Select,
  SelectItem,
  Text,
} from "@ui-kitten/components";
import Button from "components/Button";
import MainContainer from "components/MainContainer";
import ProfileAvatar from "components/ProfileAvatar";
import { LIGHT_BLUE, validationSchemas } from "constants/";
import useCurrentUser from "hooks/useCurrentUser";
import { capitalize, pick, startCase } from "lodash";
import React, { useMemo, useState } from "react";
import { Platform, StyleSheet, View } from "react-native";
import { Formik, Field } from "formik";
import TextInput from "components/TextInput";
import { useActionSheet } from "@expo/react-native-action-sheet";
import toast from "utils/toast";
import fileServices from "services/file.services";
import {
  NavigationProp,
  useIsFocused,
  useNavigation,
} from "@react-navigation/native";
import {
  options,
  cancelButtonIndex,
  actionSheetHandler,
  pickImageWithouActionSheet,
} from "utils/imagePicker";
import { AccountStackParamList } from "navigation/types";
import STATE_LIST from "constants/USAStatesList.json";
import { object } from "yup";
import useTenantUserFields, {
  getFieldsValidation,
} from "hooks/useTenantUserFields";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import { handleOnSubmitEditing } from "components/NewMemberForm";
import DatePicker from "components/DatePicker/DatePicker";
import { useAccount } from "hooks/useAccount";
import { QueryClient, useMutation } from "@tanstack/react-query";
import { updateUserFetcher } from "queries/user.queries";
import DatePickerInput from "components/DatePicker/DatePickerInput";
import { convertUserToUserFields, initial } from "utils/userUtils";
import { label } from "yet-another-react-lightbox/*";
interface userInfoType {
  [keys: string]: any;
}

const queryClient = new QueryClient();

const StateDropDown: React.FC<{
  value: string;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
}> = ({ setFieldValue, value }) => {
  const selectedIndex = value
    ? STATE_LIST.findIndex((state) => state.abbreviation === value)
    : 0;

  return (
    <Select
      onSelect={(index) =>
        setFieldValue(
          "state",
          STATE_LIST[(index as IndexPath).row].abbreviation
        )
      }
      selectedIndex={new IndexPath(selectedIndex)}
      value={STATE_LIST[selectedIndex].name}
    >
      {STATE_LIST.map((state) => (
        <SelectItem key={state.abbreviation} title={state.name}></SelectItem>
      ))}
    </Select>
  );
};

const EditProfile = () => {
  const { currentUser, userImageURL, updateCurrentUser } = useCurrentUser();
  const { showActionSheetWithOptions } = useActionSheet();
  const { account, fetchAccount } = useAccount();
  const navigation = useNavigation<NavigationProp<AccountStackParamList>>();
  const [loading, setLoading] = useState(false);
  const isFocused = useIsFocused();

  const goBack = () =>
    navigation.navigate("AccountDetails", { screen: "AccountDetailsScreen" });

  const baseUserFields = pick(currentUser, [
    "firstName",
    "lastName",
    "email",
    "phone",
    "address",
    "addressLine2",
    "city",
    "state",
    "postalCode",
  ]);
  const initialValues: userInfoType = useMemo(() => {
    if (!currentUser) {
      return {};
    }
    return {
      ...baseUserFields,
      ...currentUser.userFields,
      imageIdentifier: currentUser.imageIdentifier ? userImageURL : null,
    };
  }, [currentUser]);

  const { tenantUserFields, tenantFieldsValidation, fieldRefs } =
    useTenantUserFields();

  const userSubscriptions =
    account?.subscriptions?.filter(
      (subscription) => subscription.member.user.id === currentUser?.id
    ) ?? [];
  const subscription =
    userSubscriptions.find((subscription) => subscription.operative) ??
    userSubscriptions[0];
  const { planPackageUserFields, fieldRefs: planPackageFieldRefs } =
    usePlanPackageUserFields(subscription?.planPackage.id);

  // fields already configured at the tenant level are rendered once
  const userPlanPackageUserFields = useMemo(
    () =>
      planPackageUserFields?.filter(
        (planPackageField) =>
          !tenantUserFields?.some(
            (tenantField) =>
              tenantField.field.name === planPackageField.field.name
          )
      ),
    [planPackageUserFields, tenantUserFields]
  );
  const planPackageFieldsValidation = getFieldsValidation(
    userPlanPackageUserFields
  );

  const { mutateAsync: updateUser } = useMutation(updateUserFetcher);

  const handleFormSubmit = async (values: userInfoType) => {
    try {
      setLoading(true);
      // upload user profile photo
      let imageIdentifier: string = "";

      if (
        values.imageIdentifier !== userImageURL &&
        values.imageIdentifier !== null
      ) {
        // if user profile image has been changed
        const response = await fileServices.uploadImage(values.imageIdentifier);
        imageIdentifier = response.value;
      }
      if (!currentUser) return;
      const updatedUser = await updateUser({
        userId: currentUser!.id,
        user: {
          ...currentUser!,
          ...values,
          imageIdentifier,
          userFields: convertUserToUserFields({
            user: values as any,
            tenantOrPlanPackageUserFields: [
              ...(tenantUserFields ?? []),
              ...(userPlanPackageUserFields ?? []),
            ],
          }),
        },
      });
      updateCurrentUser(updatedUser);
      fetchAccount();
      goBack();
      toast.success("User info updated successfully");
    } catch (e) {
      console.error(e);
    } finally {
      isFocused && setLoading(false);
    }
  };

  const handlePickImage = async (
    setFieldValue: (name: string, value: any) => void,
    setFieldTouched: (
      field: string,
      isTouched?: boolean | undefined,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    if (Platform.OS === "web") {
      pickImageWithouActionSheet((uri) => {
        setFieldValue("imageIdentifier", uri);
        setFieldTouched("imageIdentifier");
      });
    } else {
      showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex,
        },
        (buttonIndex) => {
          actionSheetHandler((uri) => {
            setFieldValue("imageIdentifier", uri);
            setFieldTouched("imageIdentifier");
          }, buttonIndex);
        }
      );
    }
  };

  const { basicValidation } = validationSchemas();
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      enableReinitialize={true}
      validateOnMount={true}
      validationSchema={object({
        ...basicValidation,
        ...tenantFieldsValidation,
        ...planPackageFieldsValidation,
      })}
    >
      {({ handleSubmit, errors, values, setFieldValue, setFieldTouched }) => (
        <MainContainer title="Edit Profile" goBackAction={goBack}>
          <View style={{ padding: 15 }}>
            <View>
              <View style={{ alignSelf: "center" }}>
                <View style={{ alignItems: "center" }}>
                  <ProfileAvatar
                    source={{ uri: values.imageIdentifier }}
                    hasImageIdentifier={
                      currentUser?.imageIdentifier !== null ||
                      values.imageIdentifier
                    }
                    initial={initial(currentUser)}
                  />
                </View>
                <Button
                  testID="edit-profile-picture-button"
                  appearance="ghost"
                  onPress={() =>
                    handlePickImage(setFieldValue, setFieldTouched)
                  }
                >
                  {() => (
                    <Text style={{ color: LIGHT_BLUE }}>
                      Change Profile Photo
                    </Text>
                  )}
                </Button>
              </View>
              <Divider style={{ marginBottom: 15 }} />
              {Object.keys(baseUserFields).map((key) => {
                if (key === "imageIdentifier") return null;

                const displayValue = startCase(key) || capitalize(key);

                return (
                  <View key={key} style={styles.inputContainer}>
                    <Text category="label" style={{ flex: 0.3 }}>
                      {displayValue}
                    </Text>

                    <View style={{ flex: 0.7 }}>
                      {key === "state" ? (
                        <StateDropDown
                          setFieldValue={setFieldValue}
                          value={values.state}
                        />
                      ) : (
                        <Field
                          component={TextInput}
                          name={key}
                          appearance="default"
                          testID={`input-container-${key}`}
                        />
                      )}
                    </View>
                  </View>
                );
              })}
              {tenantUserFields?.map((tenantUserField, i) => {
                const {
                  id,
                  field: { displayName, name, type },
                } = tenantUserField;
                return (
                  <View style={styles.inputContainer} key={id}>
                    <Text category="label" style={{ flex: 0.3 }}>
                      {displayName}
                    </Text>
                    <View key={id} style={{ flex: 0.7 }}>
                      {type === "DATETIME" ? (
                        <Field component={DatePicker} name={name}></Field>
                      ) : type === "DATE" ? (
                        <Field
                          component={DatePickerInput}
                          name={name}
                          innerRef={(ref: any) => {
                            fieldRefs.current[i] = ref;
                          }}
                          userFieldType={type}
                        ></Field>
                      ) : (
                        <Field
                          component={TextInput}
                          name={name}
                          innerRef={(ref: any) => {
                            fieldRefs.current[i] = ref;
                          }}
                          userFieldType={type}
                          onSubmitEditing={() =>
                            handleOnSubmitEditing(fieldRefs.current[i + 1])
                          }
                          onKeyPress={(e: any) =>
                            handleOnSubmitEditing(fieldRefs.current[i + 1], e)
                          }
                        ></Field>
                      )}
                    </View>
                  </View>
                );
              })}
              {userPlanPackageUserFields?.map((planPackageUserField, i) => {
                const {
                  id,
                  field: { displayName, name, type },
                } = planPackageUserField;
                return (
                  <View style={styles.inputContainer} key={id}>
                    <Text category="label" style={{ flex: 0.3 }}>
                      {displayName}
                    </Text>
                    <View key={id} style={{ flex: 0.7 }}>
                      {type === "DATETIME" ? (
                        <Field component={DatePicker} name={name}></Field>
                      ) : type === "DATE" ? (
                        <Field
                          component={DatePickerInput}
                          name={name}
                          innerRef={(ref: any) => {
                            planPackageFieldRefs.current[i] = ref;
                          }}
                          userFieldType={type}
                        ></Field>
                      ) : (
                        <Field
                          component={TextInput}
                          name={name}
                          innerRef={(ref: any) => {
                            planPackageFieldRefs.current[i] = ref;
                          }}
                          userFieldType={type}
                          onSubmitEditing={() =>
                            handleOnSubmitEditing(
                              planPackageFieldRefs.current[i + 1]
                            )
                          }
                          onKeyPress={(e: any) =>
                            handleOnSubmitEditing(
                              planPackageFieldRefs.current[i + 1],
                              e
                            )
                          }
                        ></Field>
                      )}
                    </View>
                  </View>
                );
              })}
            </View>
            <Button
              testID="edit-profile-save-button"
              onPress={() => handleSubmit()}
              disabled={loading}
              loading={loading}
            >
              Save
            </Button>
          </View>
        </MainContainer>
      )}
    </Formik>
  );
};

const styles = StyleSheet.create({
  inputContainer: {
    flex: 1,
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 10,
  },
});
export default EditProfile;
